import _ from 'lodash';
import { deepFreezeObject } from '../utils/objects';

// we're tweaking the default _ merge behavior here to allow overrides to completely replace arrays
// rather than concatenating them, which is rarely what we want and easy enough to achieve by iterating
// the full desired list in the override value

// Deep merge type: recursively merges T and Z
// For arrays: Z replaces T (not merged)
// For objects: properties are recursively merged
// Z properties override T properties
type DeepMerge<T, Z> = T extends readonly any[]
  ? Z extends readonly any[]
    ? Z // Arrays are replaced, not merged
    : T
  : Z extends readonly any[]
  ? Z
  : T extends object
  ? Z extends object
    ? {
        [K in keyof T | keyof Z]: K extends keyof Z
          ? K extends keyof T
            ? DeepMerge<T[K], Z[K]>
            : Z[K]
          : K extends keyof T
          ? T[K]
          : never;
      }
    : Z
  : Z;

export function mergeAndFreezeConfigObjects<T, Z>(baseConfig: T, overrideConfig: Z): DeepMerge<T, Z> {
  const merged = _.mergeWith(_.cloneDeep(baseConfig), _.cloneDeep(overrideConfig), (objValue, srcValue) => {
    // For arrays, use override's array entirely (no element-by-element merge)
    // If override doesn't provide an array, fall back to base's array
    if (Array.isArray(srcValue) || Array.isArray(objValue)) {
      return srcValue ?? objValue;
    }
  });

  return deepFreezeObject(merged) as DeepMerge<T, Z>;
}

export enum CONFIG_INJECTION_KEYS {
  BOOKING = '__TEST_BOOKING_CONFIG__',
}

/**
 * Check if an environment value indicates a production environment.
 * Uses case-insensitive check for 'prod' to catch variations like
 * 'production', 'Production', 'PRODUCTION', 'prod', 'production-us', etc.
 */
const isProductionEnv = (envValue: string | undefined): boolean => {
  return envValue?.toLowerCase().includes('prod') ?? false;
};

/**
 * Check if the current environment allows test config injection.
 * Test config injection is only allowed in non-production environments.
 *
 * This is a security measure to prevent test overrides from being used in production.
 */
const isTestInjectionAllowed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check window-level env that apps might expose
  const windowEnv = (window as unknown as Record<string, unknown>).__VITE_APP_ENV__ as string | undefined;
  if (isProductionEnv(windowEnv)) {
    return false;
  }

  // Check if import.meta.env is available (Vite transforms this at build time)
  // Cast to any because import.meta.env only exists in Vite contexts
  try {
    const meta = import.meta as { env?: { VITE_APP_ENV?: string } };
    if (isProductionEnv(meta.env?.VITE_APP_ENV)) {
      return false;
    }
  } catch {
    // import.meta.env not available in this context
  }

  return true;
};

export const createProxyConfigObject = <T extends object>(
  getConfigWithOverrides: (overrides?: Partial<T>) => T,
  injectionKey: CONFIG_INJECTION_KEYS
): T => {
  // Helper to get injected overrides at ACCESS time (not module load time)
  // This allows tests to inject config after the module has loaded
  //
  // SECURITY: Test config injection is only allowed in non-production environments
  const getInjectedOverrides = (): Partial<T> | undefined => {
    if (!isTestInjectionAllowed()) {
      return undefined;
    }

    if ((window as any)[injectionKey]) {
      return (window as any)[injectionKey];
    }
    return undefined;
  };

  // Cache the resolved config to avoid recomputing on every property access.
  // Invalidated when the overrides reference changes (e.g., test injects new config).
  let cachedConfig: T | undefined;
  let cachedOverridesRef: Partial<T> | undefined;

  const getConfig = (): T => {
    const overrides = getInjectedOverrides();
    if (cachedConfig && overrides === cachedOverridesRef) {
      return cachedConfig;
    }
    cachedOverridesRef = overrides;
    cachedConfig = getConfigWithOverrides(overrides);
    return cachedConfig;
  };

  return new Proxy({} as T, {
    get(_target, prop) {
      return getConfig()[prop as keyof T];
    },
    ownKeys(_target) {
      return Reflect.ownKeys(getConfig());
    },
    getOwnPropertyDescriptor(_target, prop) {
      return Reflect.getOwnPropertyDescriptor(getConfig(), prop);
    },
  });
};
